import React, { lazy, Suspense, type ComponentType } from "react";
import type { Tab } from "@/contexts/tabs-context";

// View component props interface
export interface ViewComponentProps {
	workspaceId: string;
	channelId?: string;
	channel?: any;
	session?: any;
	currentWorkspace?: any;
	channels?: any[];
}

// Loading component for lazy loaded views
const ViewLoader = () => (
	<div className="flex h-full items-center justify-center">
		<div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
	</div>
);

// Error boundary for view components
class ViewErrorBoundary extends React.Component<
	{ children: React.ReactNode; viewType: string },
	{ hasError: boolean }
> {
	constructor(props: any) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error(
			`Error loading view ${this.props.viewType}:`,
			error,
			errorInfo,
		);
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="flex h-full items-center justify-center">
					<div className="text-center">
						<p className="text-destructive">
							Failed to load view: {this.props.viewType}
						</p>
						<p className="mt-2 text-muted-foreground text-sm">
							Please check the console for details
						</p>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

// Type for view component
type ViewComponent = ComponentType<ViewComponentProps>;

// Registry of view components - lazy loaded for performance
const viewRegistry: Record<string, () => Promise<{ default: ViewComponent }>> =
	{
		// Individual views
		chat: () => import("./chat-view").then((m) => ({ default: m.ChatView })),
		doc: () => import("./doc-view").then((m) => ({ default: m.DocView })),
		board: () => import("./board-view").then((m) => ({ default: m.BoardView })),
		sheet: () => import("./sheet-view").then((m) => ({ default: m.SheetView })),
		meet: () => import("./meet-view").then((m) => ({ default: m.MeetView })),
		storage: () =>
			import("./storage-view").then((m) => ({ default: m.StorageView })),
		password: () =>
			import("./password-view").then((m) => ({ default: m.PasswordView })),
		automation: () =>
			import("./automation-view").then((m) => ({ default: m.AutomationView })),
		project: () =>
			import("./project-view").then((m) => ({ default: m.ProjectView })),
		settings: () =>
			import("./settings-view").then((m) => ({ default: m.SettingsView })),

		// Overview views
		"chats-overview": () =>
			import("./chat-overview").then((m) => ({ default: m.ChatOverview })),
		"boards-overview": () =>
			import("./boards-overview").then((m) => ({ default: m.BoardsOverview })),
		"docs-overview": () =>
			import("./docs-overview").then((m) => ({ default: m.DocsOverview })),
		"sheets-overview": () =>
			import("./sheets-overview").then((m) => ({ default: m.SheetsOverview })),
		"meets-overview": () =>
			import("./meets-overview").then((m) => ({ default: m.MeetsOverview })),
		"storage-overview": () =>
			import("./storage-overview").then((m) => ({
				default: m.StorageOverview,
			})),
		"passwords-overview": () =>
			import("./passwords-overview").then((m) => ({
				default: m.PasswordsOverview,
			})),
		"automations-overview": () =>
			import("./automations-overview").then((m) => ({
				default: m.AutomationsOverview,
			})),
		"projects-overview": () =>
			import("./projects-overview").then((m) => ({
				default: m.ProjectsOverview,
			})),
		"settings-overview": () =>
			import("./settings-overview").then((m) => ({
				default: m.SettingsOverview,
			})),
		"assistant-overview": () =>
			import("./assistant-overview").then((m) => ({
				default: m.AssistantOverview,
			})),
		"workspace-overview": () =>
			import("./workspace-overview").then((m) => ({
				default: m.WorkspaceOverview,
			})),
	};

// Cache for loaded components
const componentCache = new Map<string, ViewComponent>();

// Function to get view type from tab or path
export function getViewType(tab: Tab): string {
	// Check if it's an overview page
	// Overview pages have channelId like "overview-docs", "overview-chats", etc.
	if (tab.channelId?.startsWith("overview-")) {
		const section = tab.channelId.replace("overview-", "");
		if (section === "home") {
			return "workspace-overview";
		}
		return `${section}-overview`;
	}
	
	// Check if it's an overview page (no channelId or empty channelId)
	if (!tab.channelId || tab.channelId === "") {
		// Extract the section from the path (e.g., /1/chats/ -> chats-overview)
		const pathParts = tab.path.split("/").filter(Boolean);
		if (pathParts.length >= 2) {
			const section = pathParts[1];
			// Check if this is a section overview (path ends with section name)
			if (pathParts.length === 2 || (pathParts.length === 3 && pathParts[2] === "")) {
				return `${section}-overview`;
			}
		}
		// Workspace root
		if (pathParts.length === 1) {
			return "workspace-overview";
		}
	}

	// Return the tab type for individual views
	return tab.type;
}

// Function to register a new view component dynamically
export function registerViewComponent(
	type: string,
	loader: () => Promise<{ default: ViewComponent }>,
) {
	viewRegistry[type] = loader;
	// Clear cache for this type if it exists
	componentCache.delete(type);
}

// Function to get a view component
export async function getViewComponent(
	type: string,
): Promise<ViewComponent | null> {
	// Check cache first
	if (componentCache.has(type)) {
		return componentCache.get(type)!;
	}

	// Check if component is registered
	const loader = viewRegistry[type];
	if (!loader) {
		console.warn(`No view component registered for type: ${type}`);
		return null;
	}

	try {
		// Load the component
		const module = await loader();
		const component = module.default;

		// Cache it
		componentCache.set(type, component);

		return component;
	} catch (error) {
		console.error(`Failed to load view component for type ${type}:`, error);
		return null;
	}
}

// Hook to use a view component
export function useViewComponent(type: string) {
	const [Component, setComponent] = React.useState<ViewComponent | null>(null);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<Error | null>(null);

	React.useEffect(() => {
		let cancelled = false;

		async function loadComponent() {
			setLoading(true);
			setError(null);

			try {
				const comp = await getViewComponent(type);
				if (!cancelled) {
					setComponent(() => comp);
					setLoading(false);
				}
			} catch (err) {
				if (!cancelled) {
					setError(err as Error);
					setLoading(false);
				}
			}
		}

		loadComponent();

		return () => {
			cancelled = true;
		};
	}, [type]);

	return { Component, loading, error };
}

// Component to render a view dynamically
export const DynamicView: React.FC<{
	type: string;
	props: ViewComponentProps;
}> = React.memo(({ type, props }) => {
	const { Component, loading, error } = useViewComponent(type);
	
	// Memoize the component BEFORE any conditional returns
	// This ensures hooks are always called in the same order
	const MemoizedComponent = React.useMemo(
		() => Component ? React.memo(Component) : null,
		[Component]
	);

	if (loading) {
		return <ViewLoader />;
	}

	if (error || !MemoizedComponent) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="text-center">
					<p className="text-destructive">Failed to load view: {type}</p>
					<p className="mt-2 text-muted-foreground text-sm">
						{error?.message || "Component not found"}
					</p>
				</div>
			</div>
		);
	}

	return (
		<ViewErrorBoundary viewType={type}>
			<Suspense fallback={<ViewLoader />}>
				<MemoizedComponent {...props} />
			</Suspense>
		</ViewErrorBoundary>
	);
}, (prevProps, nextProps) => {
	// Only re-render if type or essential props change
	return (
		prevProps.type === nextProps.type &&
		prevProps.props.workspaceId === nextProps.props.workspaceId &&
		prevProps.props.channelId === nextProps.props.channelId &&
		prevProps.props.channel?.id === nextProps.props.channel?.id
	);
});

// Export default registry for debugging
export const getRegisteredViews = () => Object.keys(viewRegistry);
